/**
 * AffiliateReferralBuilderPage — program builder form for tenants. Configures
 * a referral program (name, bonus-type, amount, lifetime_months, active).
 * Writes via POST /api/v1/affiliate-referral-builder/programs (upsert).
 */
import { useState, type FormEvent } from "react";
import { useAffiliateReferralBuilderCreate } from "./hooks";
import type { BonusType } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-6 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const HINT = "text-xs text-zinc-500";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const SELECT = INPUT;
const BTN_PRIMARY =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

interface FormState {
	program_id: string;
	name: string;
	bonus_type: BonusType;
	bonus_amount: string;
	lifetime_months: string;
	is_active: boolean;
}

const INITIAL: FormState = {
	program_id: "",
	name: "",
	bonus_type: "flat",
	bonus_amount: "",
	lifetime_months: "0",
	is_active: true,
};

export function AffiliateReferralBuilderPage() {
	const createMut = useAffiliateReferralBuilderCreate();
	const [form, setForm] = useState<FormState>(INITIAL);
	const [localError, setLocalError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);

	const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((s) => ({ ...s, [key]: value }));
		if (saved) setSaved(false);
		if (localError) setLocalError(null);
	};

	const validate = (s: FormState): string | null => {
		const program_id = s.program_id.trim();
		if (!SLUG_RE.test(program_id)) return "Program ID must be lowercase letters, digits, or dashes.";
		if (!s.name.trim()) return "Program name is required.";
		const amt = Number(s.bonus_amount);
		if (!Number.isFinite(amt) || amt < 0 || !Number.isInteger(amt)) {
			return "Bonus amount must be a non-negative integer.";
		}
		if (s.bonus_type === "percentage" && amt > 10000) {
			return "Percentage bonus is stored as basis points (max 10000 = 100%).";
		}
		const life = Number(s.lifetime_months);
		if (!Number.isFinite(life) || life < 0 || !Number.isInteger(life)) {
			return "Eligibility window (months) must be a non-negative integer.";
		}
		return null;
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSaved(false);
		const err = validate(form);
		if (err) {
			setLocalError(err);
			return;
		}
		createMut.mutate(
			{
				program_id: form.program_id.trim(),
				name: form.name.trim(),
				bonus_type: form.bonus_type,
				bonus_amount: Number(form.bonus_amount),
				lifetime_months: Number(form.lifetime_months),
				is_active: form.is_active,
			},
			{
				onSuccess: () => {
					setSaved(true);
				},
			},
		);
	};

	const handleReset = () => {
		setForm(INITIAL);
		setLocalError(null);
		setSaved(false);
		createMut.reset();
	};

	const amountHint =
		form.bonus_type === "flat"
			? "Flat bonus in cents (e.g. 2500 = $25.00)."
			: "Percentage in basis points (e.g. 500 = 5%). Max 10000.";

	return (
		<main
			data-testid="affiliate-referral-builder-page"
			className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Affiliate Referral Program Builder
				</h1>
				<p className="text-sm text-zinc-600">
					Configure a referral program for this tenant. Set the bonus type, amount, and eligibility window, then save to activate.
				</p>
			</header>

			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex flex-col gap-5`}
				data-testid="affiliate-referral-builder-form"
				aria-label="affiliate-referral-builder form"
				noValidate
			>
				<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="arb-program-id" className={LABEL}>
							Program ID
						</label>
						<input
							id="arb-program-id"
							type="text"
							required
							placeholder="spring-2026"
							className={INPUT}
							value={form.program_id}
							onChange={(e) => setField("program_id", e.currentTarget.value)}
							data-testid="affiliate-referral-builder-program-id-input"
							autoComplete="off"
						/>
						<span className={HINT}>Lowercase letters, digits, or dashes.</span>
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="arb-name" className={LABEL}>
							Program Name
						</label>
						<input
							id="arb-name"
							type="text"
							required
							placeholder="Spring 2026 launch"
							className={INPUT}
							value={form.name}
							onChange={(e) => setField("name", e.currentTarget.value)}
							data-testid="affiliate-referral-builder-name-input"
							autoComplete="off"
						/>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-5 md:grid-cols-3">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="arb-bonus-type" className={LABEL}>
							Bonus Type
						</label>
						<select
							id="arb-bonus-type"
							className={SELECT}
							value={form.bonus_type}
							onChange={(e) => setField("bonus_type", e.currentTarget.value as BonusType)}
							data-testid="affiliate-referral-builder-bonus-type-input"
						>
							<option value="flat">Flat (cents)</option>
							<option value="percentage">Percentage (bps)</option>
						</select>
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="arb-bonus-amount" className={LABEL}>
							Bonus Amount
						</label>
						<input
							id="arb-bonus-amount"
							type="number"
							inputMode="numeric"
							min={0}
							step={1}
							required
							placeholder="2500"
							className={INPUT}
							value={form.bonus_amount}
							onChange={(e) => setField("bonus_amount", e.currentTarget.value)}
							data-testid="affiliate-referral-builder-bonus-amount-input"
						/>
						<span className={HINT}>{amountHint}</span>
					</div>

					<div className="flex flex-col gap-1.5">
						<label htmlFor="arb-lifetime-months" className={LABEL}>
							Eligibility Window (months)
						</label>
						<input
							id="arb-lifetime-months"
							type="number"
							inputMode="numeric"
							min={0}
							step={1}
							className={INPUT}
							value={form.lifetime_months}
							onChange={(e) => setField("lifetime_months", e.currentTarget.value)}
							data-testid="affiliate-referral-builder-lifetime-months-input"
						/>
						<span className={HINT}>0 means no time limit.</span>
					</div>
				</div>

				<div className="flex items-center gap-3">
					<input
						id="arb-is-active"
						type="checkbox"
						className="h-4 w-4 rounded-[4px] border-zinc-300 text-sky-600 focus:ring-sky-500/20"
						checked={form.is_active}
						onChange={(e) => setField("is_active", e.currentTarget.checked)}
						data-testid="affiliate-referral-builder-is-active-input"
					/>
					<label htmlFor="arb-is-active" className={LABEL}>
						Active
					</label>
					<span className={HINT}>Toggle off to save as a draft (inactive).</span>
				</div>

				<div className="flex flex-wrap items-center gap-3 pt-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={createMut.isPending}
						data-testid="affiliate-referral-builder-save"
					>
						{createMut.isPending ? "Saving" : "Save Program"}
					</button>
					<button
						type="button"
						className={BTN_GHOST}
						onClick={handleReset}
						disabled={createMut.isPending}
						data-testid="affiliate-referral-builder-reset"
					>
						Reset
					</button>
					{saved ? (
						<p
							className="text-sm text-emerald-700"
							role="status"
							data-testid="affiliate-referral-builder-saved"
						>
							Program saved.
						</p>
					) : null}
					{localError ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="affiliate-referral-builder-local-error"
						>
							{localError}
						</p>
					) : null}
					{createMut.error ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="affiliate-referral-builder-error"
						>
							{createMut.error.message}
						</p>
					) : null}
				</div>
			</form>
		</main>
	);
}

export default AffiliateReferralBuilderPage;
